import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ArrowLeftRight } from 'lucide-react'

import { buildMobileBottomNavItems, isMobileBottomNavActive } from '@/config/navigation'
import { useTranslation } from '@/features/locale/LocaleProvider'
import { cn } from '@/lib/utils'

interface AppMobileBottomNavProps {
  hiddenOnMobile?: boolean
}

function BottomNavLink({
  href,
  title,
  icon: Icon,
  active,
}: {
  href: string
  title: string
  icon: React.ComponentType<{ className?: string }>
  active: boolean
}) {
  return (
    <Link
      to={href}
      className={cn(
        'pointer-events-auto flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 font-medium transition-colors',
        active ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      <span
        className={cn(
          'inline-flex size-6 items-center justify-center rounded-full',
          active ? 'bg-primary/15' : 'bg-transparent',
        )}
      >
        <Icon className="size-4" />
      </span>
      <span className="mobile-app__nav-label truncate leading-none">{title}</span>
    </Link>
  )
}

export function AppMobileBottomNav({ hiddenOnMobile = false }: AppMobileBottomNavProps) {
  const { pathname } = useLocation()
  const t = useTranslation()
  const mobileBottomNavItems = buildMobileBottomNavItems(t)

  return (
    <div
      className={cn(
        'pointer-events-none fixed inset-x-0 bottom-[max(0.5rem,env(safe-area-inset-bottom))] z-50 flex items-end justify-center gap-2 px-3 md:hidden',
        hiddenOnMobile && 'max-md:hidden',
      )}
    >
      <nav
        className="pointer-events-auto flex max-w-[min(100%,16rem)] flex-1 items-center justify-around rounded-3xl border border-white/10 bg-[#0a0c14] py-2 shadow-lg"
        aria-label="Main navigation"
      >
        {mobileBottomNavItems.map((item) => (
          <BottomNavLink
            key={item.href}
            href={item.href}
            title={item.title}
            icon={item.icon}
            active={isMobileBottomNavActive(pathname, item.href)}
          />
        ))}
      </nav>
      <Link
        to="/swap"
        className="pointer-events-auto inline-flex size-14 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/25"
        aria-label={t.brand.goToSwap}
      >
        <ArrowLeftRight className="size-6" />
      </Link>
    </div>
  )
}
